import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api_authentication } from '../../api/api-commons';
import { IS_LOCAL } from '../../constants';
import { useAuthErrorEvent } from '../../utils/hooks/useAuthErrorEvent';
import { useSecurityToken } from '../../utils/security';

const loginPath = IS_LOCAL === 'true' ? '/login' : '/login';
const MAX_RECOVERY_ATTEMPTS = 3;

type TokenHandlerProps = {
  children?: ReactNode;
};

type TokenKind = 'private' | 'public';

type State =
  | { tag: 'loading'; tokenKind: null; recoveryAttempts: number }
  | { tag: 'retrievingPublicToken'; tokenKind: null; recoveryAttempts: number }
  | {
      tag: 'synchronizingApi';
      token: string;
      tokenKind: TokenKind;
      recoveryAttempts: number;
    }
  | {
      tag: 'publicValid';
      publicToken: string;
      tokenKind: TokenKind;
      recoveryAttempts: number;
    }
  | {
      tag: 'privateValid';
      token: string;
      tokenKind: TokenKind;
      recoveryAttempts: number;
    }
  | { tag: 'failureToGetPublicToken'; recoveryAttempts: number }
  | { tag: 'invalid'; tokenKind: null; recoveryAttempts: number }
  | { tag: 'awaitingReload'; recoveryAttempts: number }
  | { tag: 'displayForbidden'; recoveryAttempts: number }
  | { tag: 'tooManyAttempts'; recoveryAttempts: number };

type Event =
  | { type: 'RouteChanged'; isPublic: boolean }
  | { type: 'TokenInUrl'; token: string; isPublic: boolean }
  | { type: 'StoredToken'; token: string | null; isPublic: boolean }
  | { type: 'NoTokenAnywhere'; isPublic: boolean }
  | { type: 'PublicTokenReceived'; token: string }
  | { type: 'PublicTokenNotReceived' }
  | { type: 'ApiSynchronized'; token: string; isPublic: boolean }
  | { type: 'InvalidPublicTokenInPublicPage' }
  | { type: 'AccessDeniedErrorReceived' };

type LoadingState = Extract<State, { tag: 'loading' }>;
type RetrievingPublicTokenState = Extract<
  State,
  { tag: 'retrievingPublicToken' }
>;
type SynchronizingApiState = Extract<State, { tag: 'synchronizingApi' }>;
type FailureToGetPublicTokenState = Extract<
  State,
  { tag: 'failureToGetPublicToken' }
>;
type PublicValidState = Extract<State, { tag: 'publicValid' }>;
type PrivateValidState = Extract<State, { tag: 'privateValid' }>;
type InvalidState = Extract<State, { tag: 'invalid' }>;
type AwaitingReloadState = Extract<State, { tag: 'awaitingReload' }>;
type DisplayForbiddenState = Extract<State, { tag: 'displayForbidden' }>;
type TooManyAttemptsState = Extract<State, { tag: 'tooManyAttempts' }>;

function toRetrievingPublicToken(recoveryAttempts: number): State {
  return { tag: 'retrievingPublicToken', tokenKind: null, recoveryAttempts };
}

function toSynchronizingApi(
  token: string,
  tokenKind: TokenKind,
  recoveryAttempts: number
): State {
  return { tag: 'synchronizingApi', token, tokenKind, recoveryAttempts };
}

function toPublicValid(publicToken: string): State {
  return {
    tag: 'publicValid',
    publicToken,
    tokenKind: 'public',
    recoveryAttempts: 0,
  };
}

function toPrivateValid(token: string): State {
  return {
    tag: 'privateValid',
    token,
    tokenKind: 'private',
    recoveryAttempts: 0,
  };
}

function toInvalid(recoveryAttempts: number): State {
  return { tag: 'invalid', tokenKind: null, recoveryAttempts };
}

function onDisplayForbidden(state: DisplayForbiddenState, event: Event): State {
  if (event.type === 'RouteChanged') {
    return event.isPublic
      ? toRetrievingPublicToken(state.recoveryAttempts)
      : toInvalid(state.recoveryAttempts);
  }
  return state;
}

function onTooManyAttempts(state: TooManyAttemptsState, _event: Event): State {
  return state;
}

function applyGlobalGuards(state: State, event: Event): State | null {
  if (state.tag === 'displayForbidden') {
    return onDisplayForbidden(state, event);
  }

  if (event.type === 'InvalidPublicTokenInPublicPage') {
    const recoveryAttempts = state.recoveryAttempts + 1;
    if (recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
      return { tag: 'tooManyAttempts', recoveryAttempts };
    }
    return { tag: 'awaitingReload', recoveryAttempts };
  }

  if (event.type === 'AccessDeniedErrorReceived') {
    return {
      tag: 'displayForbidden',
      recoveryAttempts: state.recoveryAttempts,
    };
  }

  return null;
}

function onLoading(state: LoadingState, event: Event): State {
  if (event.type === 'TokenInUrl') {
    return toSynchronizingApi(
      event.token,
      event.isPublic ? 'public' : 'private',
      state.recoveryAttempts
    );
  }

  if (event.type === 'StoredToken' && event.token) {
    return toSynchronizingApi(
      event.token,
      event.isPublic ? 'public' : 'private',
      state.recoveryAttempts
    );
  }

  if (event.type === 'RouteChanged' && event.isPublic) {
    return toRetrievingPublicToken(state.recoveryAttempts);
  }

  if (event.type === 'NoTokenAnywhere') {
    return event.isPublic
      ? toRetrievingPublicToken(state.recoveryAttempts)
      : toInvalid(state.recoveryAttempts);
  }

  return state;
}

function onRetrievingPublicToken(
  state: RetrievingPublicTokenState,
  event: Event
): State {
  if (event.type === 'PublicTokenReceived') {
    return toSynchronizingApi(event.token, 'public', state.recoveryAttempts);
  }

  if (event.type === 'PublicTokenNotReceived') {
    return {
      tag: 'failureToGetPublicToken',
      recoveryAttempts: state.recoveryAttempts,
    };
  }

  if (event.type === 'TokenInUrl') {
    return toSynchronizingApi(event.token, 'public', state.recoveryAttempts);
  }

  if (event.type === 'RouteChanged' && !event.isPublic) {
    return toInvalid(state.recoveryAttempts);
  }

  return state;
}

function onSynchronizingApi(state: SynchronizingApiState, event: Event): State {
  if (event.type === 'ApiSynchronized') {
    return event.isPublic
      ? toPublicValid(event.token)
      : toPrivateValid(event.token);
  }

  if (event.type === 'PublicTokenNotReceived') {
    return {
      tag: 'failureToGetPublicToken',
      recoveryAttempts: state.recoveryAttempts,
    };
  }

  return state;
}

function onFailureToGetPublicToken(
  state: FailureToGetPublicTokenState,
  _event: Event
): State {
  return state;
}

function onPublicValid(state: PublicValidState, event: Event): State {
  if (event.type === 'TokenInUrl') {
    return toSynchronizingApi(event.token, 'public', state.recoveryAttempts);
  }

  if (event.type === 'RouteChanged' && !event.isPublic) {
    return toInvalid(state.recoveryAttempts);
  }

  return state;
}

function onPrivateValid(state: PrivateValidState, event: Event): State {
  if (event.type === 'TokenInUrl') {
    return toSynchronizingApi(event.token, 'private', state.recoveryAttempts);
  }

  if (event.type === 'StoredToken' && !event.token) {
    return toInvalid(state.recoveryAttempts);
  }

  if (event.type === 'NoTokenAnywhere') {
    return toInvalid(state.recoveryAttempts);
  }

  return state;
}

function onInvalid(state: InvalidState, event: Event): State {
  if (event.type === 'TokenInUrl') {
    return toSynchronizingApi(
      event.token,
      event.isPublic ? 'public' : 'private',
      state.recoveryAttempts
    );
  }

  if (event.type === 'RouteChanged' && event.isPublic) {
    return toRetrievingPublicToken(state.recoveryAttempts);
  }

  return state;
}

function onAwaitingReload(state: AwaitingReloadState, _event: Event): State {
  return state;
}

export function transition(state: State, event: Event): State {
  const guardResult = applyGlobalGuards(state, event);
  if (guardResult) return guardResult;

  switch (state.tag) {
    case 'loading':
      return onLoading(state, event);
    case 'retrievingPublicToken':
      return onRetrievingPublicToken(state, event);
    case 'synchronizingApi':
      return onSynchronizingApi(state, event);
    case 'failureToGetPublicToken':
      return onFailureToGetPublicToken(state, event);
    case 'publicValid':
      return onPublicValid(state, event);
    case 'privateValid':
      return onPrivateValid(state, event);
    case 'invalid':
      return onInvalid(state, event);
    case 'awaitingReload':
      return onAwaitingReload(state, event);
    case 'displayForbidden':
      return onDisplayForbidden(state, event);
    case 'tooManyAttempts':
      return onTooManyAttempts(state, event);
  }
}

function RedirectHandler({ text, delay }: { text?: string; delay?: number }) {
  const displayText = text ?? 'Redirection en cours...';
  useEffect(() => {
    if (delay) setTimeout(() => (window.location.href = loginPath), delay);
    else window.location.href = loginPath;
  });
  return <div>{displayText}</div>;
}

function RecoveryHandler({ text }: { text?: string }) {
  const displayText = text ?? 'Reinitialisation en cours...';
  return <div>{displayText}</div>;
}

function ForbiddenHandler({ text }: { text?: string }) {
  const displayText = text ?? "Vous n'avez pas accès à cette page.";
  return <div>{displayText}</div>;
}

/**
 * TokenHandler component to manage security token from URL or session storage.
 */
export function TokenHandler({ children }: TokenHandlerProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [storedToken, setToken] = useSecurityToken();

  const [state, setState] = useState<State>({
    tag: 'loading',
    tokenKind: null,
    recoveryAttempts: 0,
  });

  const dispatchEvent = useCallback((event: Event) => {
    setState((s) => transition(s, event));
  }, []);

  const prevPath = useRef<string | null>(null);
  const isPublic = location.pathname.startsWith('/public');

  // route changes
  useEffect(() => {
    if (prevPath.current === null) {
      prevPath.current = location.pathname;
      return;
    }

    if (prevPath.current !== location.pathname) {
      dispatchEvent({
        type: 'RouteChanged',
        isPublic,
      });
      prevPath.current = location.pathname;
    }
  }, [location.pathname, isPublic, dispatchEvent]);

  // token from URL (and URL cleanup)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    if (tokenFromUrl === null) return;

    dispatchEvent({
      type: 'TokenInUrl',
      token: tokenFromUrl,
      isPublic,
    });

    params.delete('token');
    const newSearch = params.toString();
    const cleanPath = location.pathname + (newSearch ? `?${newSearch}` : '');
    navigate(cleanPath, { replace: true });
  }, [location.search, location.pathname, navigate, isPublic, dispatchEvent]);

  // token from storage fallback
  useEffect(() => {
    if (storedToken !== null && storedToken !== '') {
      dispatchEvent({
        type: 'StoredToken',
        token: storedToken,
        isPublic,
      });
    } else {
      dispatchEvent({ type: 'NoTokenAnywhere', isPublic });
    }
  }, [storedToken, isPublic, dispatchEvent]);

  // load public token
  useEffect(() => {
    if (state.tag !== 'retrievingPublicToken') return;

    let cancelled = false;

    async function loadPublicToken() {
      try {
        const url = '/login/Halias/public';
        const res = await api_authentication.post(url);
        if (res.status >= 400) {
          if (!cancelled) {
            dispatchEvent({ type: 'PublicTokenNotReceived' });
          }
          return;
        }

        const data = await res.data;
        const token = data.access_token;

        if (!cancelled) {
          dispatchEvent({
            type: 'PublicTokenReceived',
            token,
          });
        }
      } catch (err) {
        if (!cancelled) {
          dispatchEvent({ type: 'PublicTokenNotReceived' });
        }
      }
    }

    loadPublicToken();

    return () => {
      cancelled = true;
    };
  }, [state, dispatchEvent]);

  // synch all api fetchers with the new token
  useEffect(() => {
    if (state.tag !== 'synchronizingApi') return;

    setToken(state.token);
    dispatchEvent({
      type: 'ApiSynchronized',
      token: state.token,
      isPublic: state.tokenKind === 'public',
    });
  }, [state, setToken, dispatchEvent]);

  // recover from invalid public token
  useEffect(() => {
    if (state.tag !== 'awaitingReload') return;

    setToken(null);

    const params = new URLSearchParams(location.search);
    params.delete('token');
    const cleanSearch = params.toString();
    const cleanPath =
      location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
    navigate(cleanPath, { replace: true });

    setState((s) => ({
      tag: 'loading',
      tokenKind: null,
      recoveryAttempts: s.recoveryAttempts,
    }));
  }, [state, location.pathname, location.search, navigate, setToken]);

  // intercept api auth errors in public pages
  useAuthErrorEvent((status) => {
    if ([401, 498].includes(status as number)) {
      dispatchEvent({
        type: 'InvalidPublicTokenInPublicPage',
      });
    } else if (status === 403) {
      dispatchEvent({
        type: 'AccessDeniedErrorReceived',
      });
    }
  });

  // log state for debugging
  // useEffect(() => {
  //   console.debug('TokenHandler state:', state);
  // }, [state]);

  if (state.tag === 'loading') {
    return <div>Chargement...</div>;
  }

  if (['retrievingPublicToken', 'synchronizingApi'].includes(state.tag)) {
    return <div>Authorisation en cours...</div>;
  }

  if (state.tag === 'failureToGetPublicToken') {
    return (
      <RedirectHandler
        text="Redirection vers la page de connexion en cours"
        delay={3000}
      />
    );
  }

  if (state.tag === 'displayForbidden') {
    return <ForbiddenHandler />;
  }

  if (state.tag === 'tooManyAttempts') {
    return (
      <RedirectHandler
        text="Trop de tentatives de reinitialisation, redirection vers la page de connexion"
        delay={3000}
      />
    );
  }

  if (state.tag === 'awaitingReload') {
    return <RecoveryHandler />;
  }

  if (state.tag === 'invalid') {
    return <RedirectHandler />;
  }

  return children;
}
